import React from 'react'
import { CircularProgress, useTheme } from '@mui/material'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'

import UploadFile from '../UploadFile'
import UploadBox from './UploadBox'
import Media from './Media'
import MediaViewerCarousel from '@SonarCX/components/molecules/MediaViewerCarousel'
import { MAX_FILESIZE } from '@SonarCX/utilities/constants'

import { track } from '@SonarCX/lib/mixpanel'
import { formatForUpload } from './helpers'
import { uniqueArray } from '@SonarCX/utilities'
import { putUser, uploadFileUser } from '@SonarCX/lib/api'
import { selectUser } from '@SonarCX/slices/user'
import { AddErrorAlert, AddSuccessAlert } from '@SonarCX/slices/alert'
import useFileDrop from '@SonarCX/hooks/useFileDrop'
import useStyles from './styles'

type UserFilesProps = {
  id: string
}

const MAX_MEDIA = 15

const UserFiles: React.FC<UserFilesProps> = ({ id }) => {
  const theme = useTheme()
  const dispatch = useAppDispatch()

  const { users } = useAppSelector(selectUser)

  const [uploading, setUploading] = React.useState(false)
  const [viewingFile, setViewingFile] = React.useState(null)

  const classes = useStyles()
  const user = React.useMemo(() => users[id] ?? null, [users, id])

  const onDrop = React.useCallback(
    async (files: File[]) => {
      try {
        // Check that adding files won't cause API issue
        if (files.length + user.media_ids.length > MAX_MEDIA) {
          dispatch(
            AddErrorAlert({
              err: { message: `Only ${MAX_MEDIA} files can be attached` }
            })
          )
          return
        }

        // Cancel upload if any files are too big
        if (files.some(f => f.size > MAX_FILESIZE)) {
          const file = files.find(f => f.size > MAX_FILESIZE)
          dispatch(
            AddErrorAlert(
              {
                err: {
                  message: `Files must be smaller than 100MB. ${file.name} is too large`
                }
              },
              5000
            )
          )
          return
        }

        setUploading(true)

        // Upload files
        const media_ids = (
          await Promise.all(
            files.map(async file => {
              const mediaRes = await uploadFileUser(file)
              if (mediaRes) {
                return mediaRes.id
              }
              return null
            })
          )
        ).filter(Boolean)

        track('UPLOAD_USER_FILE', {
          count: media_ids.length,
          file_ids: media_ids,
          upload_user_id: user.id
        })

        // Update user with new files
        const updated = formatForUpload(user)
        updated.media_ids = uniqueArray(updated.media_ids, media_ids).filter(
          Boolean
        )

        // Save updates
        await putUser(updated)

        dispatch(AddSuccessAlert('Changes saved', 1500))
      } catch (err) {
        dispatch(AddErrorAlert({ err }))
      }

      setUploading(false)
    },
    [user, dispatch]
  )

  const handleFileSet = React.useCallback(
    (files: FileList | null) => {
      if (files) {
        onDrop([...files])
      }
    },
    [onDrop]
  )

  const { hovering } = useFileDrop({ onDrop })

  return (
    <>
      <div className={classes.root}>
        <div className={classes.title}>
          <UploadFile
            onSetFile={handleFileSet}
            fileLength={false}
            multi
            singleSet
            stroke={theme.palette.primary.main}
            className={classes.uploadIcon}
          />

          <span>Drag files here to attach to this user</span>
        </div>

        {uploading && (
          <div className={classes.loading}>
            <CircularProgress />
          </div>
        )}

        {user?.media && (
          <div className={classes.files}>
            {user.media.map((media, i) => (
              <Media
                key={`${media.id}-${i}`}
                media={media}
                user={user}
                setViewingFile={setViewingFile}
                className={classes.file}
              />
            ))}
          </div>
        )}
      </div>

      {hovering && <UploadBox />}
      {viewingFile && (
        <MediaViewerCarousel
          files={user?.media ?? []}
          file={viewingFile}
          onClose={() => setViewingFile(null)}
        />
      )}
    </>
  )
}

export default UserFiles
